import Vue from 'vue'
import VueUp from 'vueup'
import VueI18n from 'vue-i18n'
import VueIntercom from 'vue-intercom'
import VueMeta from 'vue-meta'
import VueScrollReveal from 'vue-scroll-reveal'
import VueIntro from 'vue-introjs'
import Vuelidate from 'vuelidate'
import VueMoment from 'vue-moment'
import FeatureFlags from '@/plugins/feature-flags'

import {
  MdButton,
  MdContent,
  MdTabs,
  MdField,
  MdCheckbox,
  MdDatepicker,
  MdMenu,
  MdList,
  MdProgress,
  MdAutocomplete,
  MdTooltip
} from 'vue-material/dist/components'
import 'vue-material/dist/vue-material.min.css'
import 'vue-material/dist/theme/default.css'
import 'flexboxgrid/dist/flexboxgrid.min.css'
import 'tether-drop/dist/css/drop-theme-arrows.css'
import 'intro.js/introjs.css'
import 'swiper/dist/css/swiper.css'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { polyfillLoader } from 'polyfill-io-feature-detection'
import rg4js from 'raygun4js'
import app from '@/app'
import router from '@/router'
import store from '@/store'
import '@/register-components'
import '@/register-directives'
import '@/highcharts-config'
import Formatter, { dateTimeFormats, numberFormats } from '@/utils/formatter'
import { Analytics } from '@/utils/analytics'
import { AppHolder } from '@/utils/app-holder'
import configFile from '@/../app-config.js'
import localeFR from '@/../locales/fr.json'

export const defaultLocale = configFile[process.env.APP_ID].params.defaultLocale
export const bus = new Vue()

AppHolder.bus = bus

const bootstrapApp = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => navigator.serviceWorker.register('/serviceWorker.js', {
      scope: '/'
    }))
  }

  Vue.use(VueUp)
  Vue.use(VueI18n)
  Vue.use(VueScrollReveal)
  Vue.use(VueMeta)
  Vue.use(VueIntro)
  Vue.use(Vuelidate)
  Vue.use(MdButton)
  Vue.use(MdContent)
  Vue.use(MdTabs)
  Vue.use(MdField)
  Vue.use(MdCheckbox)
  Vue.use(MdDatepicker)
  Vue.use(MdMenu)
  Vue.use(MdList)
  Vue.use(MdProgress)
  Vue.use(MdAutocomplete)
  Vue.use(MdTooltip)
  Vue.use(VueMoment)
  Vue.use(FeatureFlags, configFile[process.env.APP_ID].features)

  if (process.env.RAYGUN_ID) {
    rg4js('apiKey', process.env.RAYGUN_ID)
    rg4js('filterSensitiveData', ['password', 'credit_card'])
    Vue.config.errorHandler = function (err, vm, info) {
      rg4js('send', err)
    }
    rg4js('enableCrashReporting', true)
    rg4js('enablePulse', true)
    router.afterEach(to => {
      rg4js('trackEvent', {
        type: 'pageView',
        path: to.path
      })
    })
    store.watch(state => state.user.profile, value => {
      if (value && value.email) {
        rg4js('setUser', {
          identifier: value.email,
          isAnonymous: false,
          email: value.email,
          fullName: (value.firstName && value.lastName) ? `${value.firstName} ${value.lastName}` : value.email,
          firstName: value.firstName,
          uuid: value.id
        })
      }
    })
  }
  if (process.env.INTERCOM_ID) {
    Vue.use(VueIntercom, { appId: process.env.INTERCOM_ID })
  }
  if (Analytics.isAvailable()) {
    router.afterEach(to => {
      Analytics.page(to.name, to.path)
    })
    store.watch(state => state.user.profile, value => {
      if (value && value.email) {
        Analytics.identify(value.id, {
          name: (value.firstName && value.lastName) ? `${value.firstName} ${value.lastName}` : value.email,
          email: value.email
        })
      }
    })
  }

  Vue.config.productionTip = false

  // Set lang and locales
  const locales = {
    'fr-FR': localeFR
  }

  const formatter = new Formatter({ locale: defaultLocale })

  const i18n = new VueI18n({
    locale: defaultLocale,
    fallbackLocale: defaultLocale,
    formatter,
    messages: locales,
    dateTimeFormats,
    numberFormats
  })

  /* eslint-disable no-new */
  new Vue({
    el: '#app',
    i18n,
    router,
    store,
    render: h => h(app)
  })
}

polyfillLoader({
  'features': 'Promise,String.prototype.includes,Array.prototype.find,Object.keys,Intl,Element.prototype.classList,Array.prototype.includes,Array.prototype.keys',
  'onCompleted': bootstrapApp
})
